// One wire shape per entity, stated once and consumed by every door.
//
// A response body used to be declared three times in this product: a
// hand-written projection in the REST route, a second one in the MCP verb, and
// a hand-typed mirror in the dashboard. Nothing mechanical kept them equal, and
// measurement said they were not: the route's `RunnerRosterResource` projected
// 19 keys while the client's `RunnerRow` (`apps/dashboard/src/lib/fleet.ts`)
// declared 16 — three fields the API sent were invisible to the client's type,
// two more disagreed on optionality, and
// `bun --filter @developerz/dashboard typecheck` exited 0 over all five. It could
// not see them, because the mirror was a cast (`api.ts`,
// `return (await res.json()) as T`) rather than a parse.
//
// The first two copies are gone for the entities that have a resource here —
// `RunnerResource`, `RunnerRosterResource` and `RunnerRevocationResource`
// became `wire/runner.ts` (#3433) — and each one deleted was a chance for the
// drift to be found rather than shipped: `projectCiRun` and `maskCiJob`
// disagreed by two fields, and `refusal_reason` was one of them, so an agent
// asking why a CI run was refused was told nothing. The DASHBOARD copy is
// gone where it adopted these schemas (the runner roster parses through
// `RunnerRosterWireSchema`, `apps/dashboard/src/lib/fleet.ts`); a body it
// still types by hand is still a mirror.
//
// A `Resource` is the fix and it is deliberately small: a schema plus the
// function that produces a value of it. The type parameter is what does the
// work — `serialize` is checked against `z.infer<typeof schema>`, so a
// projection that adds, drops or renames a field stops compiling at the seam
// instead of arriving as `undefined` in a browser.
//
// ## Why `domain`
//
// Not "the only package both apps depend on" — that is false, the intersection
// of `apps/api` and `apps/dashboard` is `{config, domain, kit}`. The reason is
// that `domain` is where entities already live (`entities/`, `value-objects/`,
// `capability-manifest.ts`); `kit` is runtime primitives and a wire shape is not
// one; `config` imports `domain` and so is downstream of it. A response body is
// an entity's public projection, so it belongs beside the entity.
//
// ## Two constraints, both learned the hard way
//
// - **No `.transform()` anywhere in a resource schema.** `z.toJSONSchema`
//   throws on one, and publishing these into OpenAPI is the point of having
//   them typed. `defineResource` cannot enforce this structurally, so
//   `pinResource` proves it per resource by actually calling `toJSONSchema`.
// - **The wire is snake_case, the entity is camelCase.** That boundary is where
//   the drift lives, so the schema names the wire spelling and `serialize` is
//   the only place the rename happens.

import { z } from 'zod';

/**
 * A wire shape and the projection that produces it.
 *
 * `Wire` is inferred from `schema`, so `serialize` cannot return a different
 * shape than the schema publishes — the property the three hand-written copies
 * had no way to state.
 */
export interface Resource<Entity, Schema extends z.ZodType = z.ZodType> {
  /** The published body. Snake_case, transform-free, `z.toJSONSchema`-able. */
  readonly schema: Schema;
  /** The one projection. Every door calls this rather than re-deriving it. */
  readonly serialize: (entity: Entity) => z.infer<Schema>;
}

/** The wire type a resource publishes — what a client should type against. */
export type WireOf<R> = R extends Resource<infer _E, infer S> ? z.infer<S> : never;

/**
 * Declare a resource.
 *
 * Exists so `Entity` can be given explicitly while `Wire` stays inferred from
 * the schema — TypeScript has no partial type-argument inference, and the
 * alternative is every call site restating the wire type it just wrote.
 */
export function defineResource<Entity, Schema extends z.ZodType>(input: {
  schema: Schema;
  serialize: (entity: Entity) => z.infer<Schema>;
}): Resource<Entity, Schema> {
  return { schema: input.schema, serialize: input.serialize };
}

/** What {@link checkResource} found wrong. Empty means the resource is sound. */
export type ResourceProblem =
  | { readonly kind: 'schema_rejected_output'; readonly issues: readonly string[] }
  | { readonly kind: 'extra_keys'; readonly keys: readonly string[] }
  | { readonly kind: 'missing_keys'; readonly keys: readonly string[] }
  | { readonly kind: 'not_json_schemable'; readonly error: string };

/**
 * Grade one resource against one sample entity — the golden-shape check the
 * wire contract needs before either hand-written copy is deleted.
 *
 * Four questions, because each catches a different real break:
 *
 * - does `serialize`'s output satisfy its own schema? (a projection that lies)
 * - does it emit a key the schema never published? (a field leaking to clients
 *   — the direction that matters for a private field)
 * - does it omit a key the schema requires? (the `RunnerRow` direction: the
 *   client's type promises something the body does not carry)
 * - can the schema reach OpenAPI at all? (`z.toJSONSchema` throws on a
 *   `.transform()`, and a resource that cannot be published is half a seam)
 *
 * Returns problems rather than throwing, so a caller can report every one at
 * once instead of fixing them one run at a time.
 */
export function checkResource<Entity, Schema extends z.ZodType>(
  resource: Resource<Entity, Schema>,
  sample: Entity,
): ResourceProblem[] {
  const problems: ResourceProblem[] = [];
  const output = resource.serialize(sample) as Record<string, unknown>;

  const parsed = resource.schema.safeParse(output);
  if (!parsed.success) {
    problems.push({
      kind: 'schema_rejected_output',
      issues: parsed.error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`),
    });
  }

  // Compare against the schema's OWN key list rather than the parsed value:
  // an object schema strips unknown keys, so a leaked field would parse
  // cleanly and vanish from the comparison — the check would then be green
  // exactly when it matters.
  const declared = declaredKeys(resource.schema);
  if (declared !== null) {
    const emitted = Object.keys(output);
    // `extra` is graded against EVERY declared key; `missing` only against the
    // REQUIRED ones. An `.optional()` key that a variant does not emit is the
    // published contract, not a break — a roster row carries the cross-org
    // block and a single-box body does not, and calling that "missing" would
    // make the check fail on exactly the resources it is most needed for.
    // `.nullable()` is untouched by this: a nullable field must still be
    // EMITTED, as null.
    const extra = emitted.filter((k) => !declared.required.has(k) && !declared.optional.has(k));
    const missing = [...declared.required].filter((k) => !emitted.includes(k));
    if (extra.length > 0) problems.push({ kind: 'extra_keys', keys: extra });
    if (missing.length > 0) problems.push({ kind: 'missing_keys', keys: missing });
  }

  try {
    z.toJSONSchema(resource.schema);
  } catch (err) {
    problems.push({
      kind: 'not_json_schemable',
      error: err instanceof Error ? err.message : String(err),
    });
  }

  return problems;
}

/**
 * The keys an object schema publishes, split by whether the body must carry
 * them — or `null` when the schema is not an object (a union body, say) and a
 * key comparison would be meaningless.
 *
 * `null` rather than two empty sets on purpose: empty sets would make every
 * emitted key read as "extra" and every resource fail for the wrong reason.
 *
 * Optionality is read by ASKING the field schema whether it admits `undefined`,
 * rather than by matching on a zod internal name. `.optional()` does, and
 * `.nullable()` does not — which is exactly the distinction the caller needs,
 * and it survives a wrapper (`.optional().describe(...)`) that a type-name
 * check would miss.
 */
function declaredKeys(
  schema: z.ZodType,
): { readonly required: ReadonlySet<string>; readonly optional: ReadonlySet<string> } | null {
  const shape = (schema as { shape?: Record<string, z.ZodType> }).shape;
  if (shape === undefined) return null;
  const required = new Set<string>();
  const optional = new Set<string>();
  for (const [key, field] of Object.entries(shape)) {
    (field.safeParse(undefined).success ? optional : required).add(key);
  }
  return { required, optional };
}
